import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const CreativeWaysUseNFCCard = () => {
  useSEO({
    title: "Creative Ways to Use Your NFC Business Card | AM CARDMATE Blog",
    description: "Discover innovative and creative ways to maximize your NFC business card's potential beyond traditional networking.",
    image: "/assets/creative.jpg",
    keywords: "creative NFC uses, innovative networking, NFC business card ideas, digital card tips"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": "10 Creative Ways to Use Your NFC Business Card",
        "description": "Discover innovative ways to maximize your NFC business card's potential",
        "datePublished": "2025-05-30",
        "dateModified": "2025-05-30",
        "author": { "@type": "Organization", "name": "AM CARDMATE Marketing Team" },
        "publisher": { "@type": "Organization", "name": "AM CARDMATE", "logo": { "@type": "ImageObject", "url": "https://amcardmate.com/assets/logo.svg" } },
        "image": "https://amcardmate.com/assets/creative.jpg",
        "mainEntityOfPage": { "@type": "WebPage", "@id": "https://amcardmate.com/blog/creative-ways-use-nfc-card" }
      },
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Use NFC Business Cards Creatively",
        "description": "Step-by-step creative uses for NFC cards",
        "step": [
          { "@type": "HowToStep", "name": "Conference Ice Breaker", "text": "Approach groups with 'Want to see something cool?' then tap your card" },
          { "@type": "HowToStep", "name": "Signature Move", "text": "End every meeting with a memorable tap" }
        ]
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [showAppPopup, setShowAppPopup] = useState(false);

  const creativeIdeas = [
    { number: 1, title: "The Conference Ice Breaker", content: "At networking events, approach groups with: \"Want to see something cool?\" Then tap your card on their phone. Instant conversation starter! The \"wow\" factor breaks the ice better than any pickup line.", tip: "Follow up with \"I'm [Your Name], and I help companies [Your Value Proposition].\" Now you've got their attention AND their contact!", color: 'from-blue-400 to-blue-600' },
    { number: 2, title: "The Signature Move", content: "Make it your signature. End every meeting with: \"Before you go...\" *tap* \"Now you have everything.\" It becomes your thing. People will actually look forward to it!", tip: "One user reported that clients started introducing him as \"the guy with the cool tap card\" – instant memorable branding!", color: 'from-purple-400 to-purple-600' },
    { number: 3, title: "The Portfolio Showcase", content: "Link your card to a dynamic portfolio page. Photographers, designers, consultants – update your best work regularly. When someone taps your card, they see your latest projects.", tip: "Update it right before important meetings with relevant case studies. Personalized portfolios = impressed clients!", color: 'from-pink-400 to-pink-600' },
    { number: 4, title: "The Event Check-In Revolution", content: "Hosting an event? Place your NFC card at the registration desk. Attendees tap to instantly get event info, WiFi passwords, agenda, and your contact.", tip: "Create event-specific profiles with schedules, speaker bios, and exclusive attendee offers.", color: 'from-green-400 to-green-600' },
    { number: 5, title: "The Social Media Growth Hack", content: "Configure your card to show a special landing page with all your social media links and a compelling reason to follow. Watch your follower count soar!", tip: "Offer a free resource (ebook, checklist, template) for new connections. Value first, sales later!", color: 'from-orange-400 to-orange-600' },
    { number: 6, title: "The Restaurant/Retail Power Play", content: "Own a business? Put NFC cards at every table or checkout. Customers tap for instant access to menus, loyalty programs, or exclusive deals.", tip: "Link to your online ordering system or appointment booking. Make it easy to buy from you again!", color: 'from-teal-400 to-teal-600' },
    { number: 7, title: "The Follow-Up Genius", content: "After meetings, mail your NFC card with a handwritten note. \"Great meeting you! Tap for the resources we discussed.\" It's unexpected and memorable.", tip: "Include case studies or proposals specific to your discussion. Personalization wins deals!", color: 'from-red-400 to-red-600' },
    { number: 8, title: "The Presentation Finisher", content: "End every presentation with: \"Want the slides and my contact?\" Have audience members come tap your card. You'll create a line of people wanting to connect!", tip: "Promise exclusive bonus content for those who tap. Everyone loves insider info!", color: 'from-indigo-400 to-indigo-600' },
    { number: 9, title: "The Team Amplifier", content: "Get cards for your whole team with a team landing page. When anyone taps a team member's card, they see the entire team's contacts.", tip: "Track which team member generates the most connections. Friendly competition drives results!", color: 'from-yellow-400 to-yellow-600' },
    { number: 10, title: "The Car Door Closer", content: "For sales pros: Leave your NFC card with reception desks, in waiting rooms, or with key contacts. You're networking even when you're not there!", tip: "One realtor left cards at coffee shops with \"Free Home Value Calculator\" – generated 5 leads per week!", color: 'from-cyan-400 to-cyan-600' }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-white via-orange-50/20 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-2 text-caption mb-6">
              <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <Link to="/blog" className="hover:text-primary-blue transition-colors">Blog</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <span className="text-primary-blue">Creative Ways to Use NFC</span>
            </nav>

            <div className="text-center mb-8">
              <div className="badge badge-pink inline-flex mb-6">
                <FontAwesomeIcon icon="lightbulb" />
                <span>Business Tips</span>
              </div>

              <h1 className="text-h1 mb-6">
                10 Creative Ways to Use Your
                <span className="block text-gradient-blue-pink mt-2">NFC Business Card</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-caption">
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="user" className="text-primary-blue" />
                  Marketing Team
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="calendar" className="text-primary-blue" />
                  May 30, 2025
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="clock" className="text-primary-blue" />
                  7 min read
                </span>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12">
              <img src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=1200&h=600&fit=crop" alt="Creative networking with NFC cards" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Intro */}
            <p className="text-body-lg text-gray-600 mb-8">
              So you've got your shiny new AM CARDMATE NFC business card. You've mastered the basic tap-and-share. But wait – there's so much more! Let's explore creative ways to turn your smart card into a networking superweapon that'll make you unforgettable. 🚀
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 md:p-8 mb-12">
              <h3 className="text-h4 text-dark mb-4 flex items-center gap-3">
                <FontAwesomeIcon icon="magic" className="text-purple-600" />
                Beyond the Basics
              </h3>
              <p className="text-body text-gray-600">
                Your NFC card isn't just a contact sharer – it's a conversation starter, a brand builder, and sometimes even a party trick. Ready to level up your networking game? Let's dive in!
              </p>
            </div>

            {/* Creative Ideas */}
            <div className="text-center mb-12">
              <h2 className="text-h2 mb-6">The Ultimate Creative <span className="text-gradient-blue-pink">Playbook</span></h2>
            </div>

            <div className="space-y-6 mb-16">
              {creativeIdeas.map((idea, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${idea.color} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                      <span className="text-h5 font-bold">{idea.number}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-h4 text-dark mb-3">{idea.title}</h3>
                      <p className="text-body text-gray-600 mb-4">{idea.content}</p>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-body-sm text-gray-600">
                          <strong>Pro tip:</strong> {idea.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bonus Tips */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-pink-500 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="star" />
                </div>
                <h2 className="text-h3 text-dark">Bonus Pro Tips</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: 'sync', color: 'text-primary-blue', bg: 'from-blue-50 to-purple-50', title: 'Keep It Fresh', desc: 'Update your profile regularly. Add recent wins, new services, or timely content. Your card should be as dynamic as you are!' },
                  { icon: 'chart-line', color: 'text-pink-500', bg: 'from-pink-50 to-orange-50', title: 'Track Everything', desc: "Use AM CARDMATE's analytics to see what works. Which approach gets more taps? What content converts? Data drives success!" },
                  { icon: 'users', color: 'text-green-500', bg: 'from-green-50 to-teal-50', title: 'Make It Social', desc: 'Share your tap experiences on social media. Tag #AMCardmate and watch others get inspired by your creativity!' },
                  { icon: 'magic', color: 'text-purple-500', bg: 'from-purple-50 to-blue-50', title: 'Be Memorable', desc: "The tap is just the beginning. What matters is the experience you create around it. Make it uniquely yours!" }
                ].map((tip, i) => (
                  <div key={i} className={`bg-gradient-to-br ${tip.bg} rounded-2xl p-6`}>
                    <h3 className="text-card-title text-dark mb-3 flex items-center gap-2">
                      <FontAwesomeIcon icon={tip.icon} className={tip.color} />
                      {tip.title}
                    </h3>
                    <p className="text-card-body">{tip.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Success Stories */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="trophy" />
                </div>
                <h2 className="text-h3 text-dark">Real Success Stories</h2>
              </div>

              <div className="space-y-6">
                {[
                  { quote: "I started ending every Zoom call with 'I'll tap my card to the camera - screenshot for my info!' It became my signature move. Clients love it and always remember me!", author: "Sarah M., Marketing Consultant", gradient: 'from-blue-400 to-blue-600' },
                  { quote: "We put NFC cards at our trade show booth with a contest entry. Collected 500+ leads in 3 days. Best ROI ever!", author: "Mike T., Sales Director", gradient: 'from-purple-400 to-purple-600' },
                  { quote: "I mail my card to prospects before calls. When we meet, they already have my info and are impressed by the tech. Close rate up 30%!", author: "Jennifer L., B2B Sales", gradient: 'from-green-400 to-green-600' }
                ].map((story, i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-md p-6 md:p-8">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 bg-gradient-to-br ${story.gradient} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
                        <FontAwesomeIcon icon="quote-right" className="text-sm" />
                      </div>
                      <div>
                        <p className="text-body text-gray-600 mb-3 italic">"{story.quote}"</p>
                        <p className="text-body-sm text-gray-500 font-semibold">— {story.author}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#6FB1FF] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#DE6161]/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 mb-4">
              <span className="text-white">Ready to Get </span>
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">Creative?</span>
            </h2>
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Your NFC business card is limited only by your imagination. Every tap is an opportunity to create a memorable experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/nfc-cards" className="btn bg-white text-[#4DA2FF] hover:bg-gray-100 transition-all shadow-lg">
                <FontAwesomeIcon icon="shopping-cart" />
                <span>Get Your Creative Card</span>
              </Link>
              <button onClick={() => setShowAppPopup(true)} className="btn bg-white/20 text-white hover:bg-white/30 transition-all">
                <FontAwesomeIcon icon="user" />
                <span>Create Your Profile</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Author & Related */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Author */}
            <div className="border-t pt-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-yellow-400 flex items-center justify-center text-white font-bold text-body-lg flex-shrink-0">MT</div>
                <div>
                  <h3 className="text-card-title text-dark">Marketing Team</h3>
                  <p className="text-body-sm text-gray-500 mb-2">AM CARDMATE Creative Department</p>
                  <p className="text-body-sm text-gray-600">
                    We're the creative minds behind AM CARDMATE's most innovative campaigns. When we're not dreaming up new ways to use NFC cards, we're probably tapping our cards on random objects to see what happens!
                  </p>
                </div>
              </div>
            </div>

            {/* Related Posts */}
            <div>
              <h3 className="text-h3 text-dark mb-6">Keep Exploring</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { to: '/blog/how-nfc-business-cards-work', title: 'How NFC Business Cards Work', gradient: 'from-blue-500 to-purple-600' },
                  { to: '/blog/track-networking-roi', title: 'Track Your Networking ROI', gradient: 'from-teal-400 to-blue-500' },
                  { to: '/blog/future-of-networking', title: 'The Future of Networking', gradient: 'from-purple-500 to-pink-500' }
                ].map((post, i) => (
                  <Link key={i} to={post.to} className={`group ${i === 2 ? 'sm:col-span-2 md:col-span-1' : ''}`}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`}></div>
                      <div className="p-4">
                        <h4 className="text-body font-bold text-dark group-hover:text-primary-blue transition-colors">{post.title}</h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Popup */}
      {showAppPopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAppPopup(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon="mobile-alt" className="text-white text-3xl" />
              </div>
              <h3 className="text-h3 text-gray-900 mb-4">Download AM CARDMATE</h3>
              <p className="text-body text-gray-600 mb-8">Create your digital profile in the app</p>
              <div className="space-y-3 mb-6">
                <a href="https://apps.apple.com/pt/app/am-cardmate/id6747951046" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-black text-white px-6 py-4 rounded-xl hover:bg-gray-900 transition-all w-full">
                  <FontAwesomeIcon icon={['fab', 'apple']} className="text-2xl" />
                  <div className="text-left flex-1">
                    <div className="text-caption opacity-80">Download on the</div>
                    <div className="text-btn-lg">App Store</div>
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.amcardmate.app" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-black text-white px-6 py-4 rounded-xl hover:bg-gray-900 transition-all w-full">
                  <FontAwesomeIcon icon={['fab', 'google-play']} className="text-2xl" />
                  <div className="text-left flex-1">
                    <div className="text-caption opacity-80">Get it on</div>
                    <div className="text-btn-lg">Google Play</div>
                  </div>
                </a>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl text-btn hover:from-blue-600 hover:to-purple-700 transition-all" onClick={() => setShowAppPopup(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreativeWaysUseNFCCard;